import React, { useContext, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from '../api/axiosConfig';
import './Navbar.css';

const Navbar = ({ title }) => {
  const { user, logout } = useContext(AuthContext);
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Estados de Notificaciones
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notiOpen, setNotiOpen] = useState(false);
  const notiRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleMyProfile = () => {
    navigate('/my-profile');
    setDropdownOpen(false);
  };

  // Obtener notificaciones desde el backend
  const fetchNotifications = async () => {
    if (!user) return;
    try {
      const [countRes, listRes] = await Promise.all([
        axios.get('/api/notifications/unread-count'),
        axios.get('/api/notifications')
      ]);
      setUnreadCount(countRes.data.count);
      setNotifications(listRes.data);
    } catch (error) {
      console.error('Error al obtener notificaciones:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();

    // Polling de notificaciones cada 15 segundos
    const interval = setInterval(() => {
      fetchNotifications();
    }, 15000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Manejadores de Notificaciones
  const handleMarkAsRead = async (noti) => {
    try {
      if (!noti.read) {
        await axios.put(`/api/notifications/${noti.id}/read`);
        fetchNotifications();
      }
      setNotiOpen(false);
      if (noti.link) {
        navigate(noti.link);
      }
    } catch (error) {
      console.error('Error al marcar notificación como leída:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await axios.put('/api/notifications/read-all');
      fetchNotifications();
    } catch (error) {
      console.error('Error al marcar todas como leídas:', error);
    }
  };

  const handleDeleteNoti = async (e, id) => {
    e.stopPropagation();
    try {
      await axios.delete(`/api/notifications/${id}`);
      fetchNotifications();
    } catch (error) {
      console.error('Error al eliminar notificación:', error);
    }
  };

  const formatRelativeTime = (dateStr) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Hace un momento';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`;
    return `Hace ${diffDays} ${diffDays === 1 ? 'día' : 'días'}`;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (notiRef.current && !notiRef.current.contains(event.target)) {
        setNotiOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <nav className="navbar">
      <div className="navbar-content">
        <div className="navbar-left">
          <h1>{title || 'TRANSIAPP'}</h1>
        </div>
        
        <div className="navbar-right">
          {/* Campana de Notificaciones */}
          <div className="notifications-dropdown-wrapper" ref={notiRef}>
            <button 
              className={`notifications-trigger-btn ${unreadCount > 0 ? 'has-unread' : ''}`}
              onClick={() => setNotiOpen(!notiOpen)}
              title="Notificaciones"
            >
              🔔
              {unreadCount > 0 && <span className="notifications-badge">{unreadCount}</span>}
            </button>

            {notiOpen && (
              <div className="notifications-panel">
                <div className="notifications-panel-header">
                  <h3>Notificaciones</h3>
                  {unreadCount > 0 && (
                    <button onClick={handleMarkAllAsRead} className="btn-mark-all-read">
                      ✔ Marcar todo leído
                    </button>
                  )}
                </div>
                
                <div className="notifications-list">
                  {notifications.length > 0 ? (
                    notifications.map(noti => (
                      <div 
                        key={noti.id} 
                        className={`notification-item ${noti.read ? 'read' : 'unread'} noti-${noti.type}`}
                        onClick={() => handleMarkAsRead(noti)}
                      >
                        <div className="noti-icon">
                          {noti.type === 'success' && '✅'}
                          {noti.type === 'warning' && '⚠️'}
                          {noti.type === 'error' && '❌'}
                          {noti.type === 'info' && 'ℹ️'}
                        </div>
                        <div className="noti-content">
                          <h4 className="noti-title">{noti.title}</h4>
                          <p className="noti-message">{noti.message}</p>
                          <span className="noti-time">{formatRelativeTime(noti.created_at)}</span>
                        </div>
                        <button 
                          className="btn-delete-noti"
                          onClick={(e) => handleDeleteNoti(e, noti.id)}
                          title="Eliminar"
                        >
                          &times;
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="notifications-empty">
                      🔔 No tienes notificaciones
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <button 
            className="theme-toggle-btn-nav" 
            onClick={toggleTheme} 
            title={theme === 'light' ? 'Activar Modo Oscuro' : 'Activar Modo Claro'}
          >
            {theme === 'light' ? '🌙' : '☀️'}
          </button>

          <div className="user-dropdown" ref={dropdownRef}>
            <button 
              className="user-dropdown-btn"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <div className="user-avatar">
                {user?.username?.charAt(0).toUpperCase()}
              </div>
              <div className="user-details-dropdown">
                <span className="username">{user?.username}</span>
                <span className={`role role-${user?.role}`}>
                  {user?.role}
                </span>
              </div>
              <span className="dropdown-arrow">{dropdownOpen ? '▲' : '▼'}</span>
            </button>

            {dropdownOpen && (
              <div className="dropdown-menu">
                <button onClick={handleMyProfile} className="dropdown-item">
                  👤 Mi Cuenta
                </button>
                <div className="dropdown-divider"></div>
                <button onClick={handleLogout} className="dropdown-item logout-item">
                  🚪 Cerrar Sesión
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
